import {
  Button,
  DatePicker,
  Input,
  Typography,
  IconButton
} from '@fullscreendigital/carrefour-dex-fe'
import { useFormik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNotification } from 'hooks'
import { useQuery, useQueryClient } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from 'utils/api'
import { formatErrorMessage } from 'utils/formatting'
import { CircularProgress, Grid, Paper, TableContainer, Box } from '@mui/material'
import { TitleHeader } from 'components/shop-reception/mobile/Common/TitleHeader'
import { ManageSearch as ManageSearchIcon } from '@mui/icons-material'
import { URLS } from 'components/shop-reception/mobile/constants'
import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow, Autocomplete, TextField } from '@mui/material'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import { DefectReason, Mark, ScanStatus } from '../../../../../../../enums'
import AlertModal from 'components/shop-reception/mobile/Common/AlertModal/AlertModal'
import dayjs from 'dayjs'
import { validateScanCode } from '../../../../../../helpers'
import { toProductOptions, findSelectedOption } from '../../serializers'
import FlexCenter from '../../../../../../../Common/FlexCenter/FlexCenter'
import { makeScanAwareDecimalChange } from "./helpers"

interface IProductScanModel {
  scanSuccess?: boolean
  errorMessage?: string
  productId?: number
  productName?: string
  productCode?: string
  productEans?: string
  quantity?: number
  validated?: number
  broken?: number
  shortDlc?: boolean
  dlc?: Date
  defectReason?: DefectReason
  mark?: Mark
  shopReceptionDeviation?: number
  scanStatus?: ScanStatus
  sgr?: boolean
}

const DeliveryCompleteScanStep2 = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()
  const markOptions = Object.keys(Mark).map(key => ({ value: key, label: Mark[key] }))
  const defectReasonOptions = Object.keys(DefectReason).map(key => ({
    value: key,
    label: DefectReason[key]
  }))

  const [isSgrProductsModalOpen, setIsSgrProductsModalOpen] = useState(false)
  const [sgrProductsModalContent, setSgrProductsModalContent] = useState({
    title: '',
    listItems: [],
    buttons: []
  })
  const [isProductScanModalOpen, setIsProductScanModalOpen] = useState(false)
  const [productScanModalContent, setProductScanModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isEanNotPresentModalOpen, setIsEanNotPresentModalOpen] = useState(false)
  const [eanNotPresentModalContent, setEanNotPresentModalContent] = useState({
    title: '',
    buttons: []
  })
  const [isCodeAndEanNotPresentModalOpen, setIsCodeAndEanNotPresentModalOpen] = useState(false)
  const [codeAndEanNotPresentModalContent, setCodeAndEanNotPresentModalContent] = useState({
    title: '',
    buttons: []
  })
  const [isSgrMessageModalOpen, setIsSgrMessageModalOpen] = useState(false)
  const [sgrMessageModalContent, setSgrMessageModalContent] = useState({ title: '', buttons: [] })
  const [isMissingProductsConfirmModalOpen, setIsMissingProductsConfirmModalOpen] = useState(false)
  const [MissingProductsConfirmModalContent, setMissingProductsConfirmModalContent] = useState({
    title: '',
    listItems: [],
    buttons: []
  })
  const [isWrongQuantitiesModalOpen, setIsWrongQuantitiesModalOpen] = useState(false)
  const [wrongQuantitiesModalContent, setWrongQuantitiesModalContent] = useState({ title: '', description: '', buttons: [] })
  const [isShortLifeModalOpen, setIsShortLifeModalOpen] = useState(false)
  const [shortLifeModalContent, setShortLifeModalContent] = useState({ title: '', description: '', buttons: [] })

  const [marks, setMarks] = useState<string[]>([])
  const [defectReasons, setDefectReasons] = useState([])
  const [litigation, setLitigation] = useState(null)
  const [showModificaButton, setShowModificaButton] = useState(false)
  const [productCode, setProductCode] = useState('')
  const [imageUploaded, setImageUploaded] = useState(false)
  const [confirmResponse, setConfirmResponse] = useState({ counter: 0, total: 0 })
  const [isWrongDlcAlertOpen, setIsWrongDlcAlertOpen] = useState(false)
  const [isMandatoryDlcAlertOpen, setIsMandatoryDlcAlertOpen] = useState(false)
  const [showMarks, setShowMarks] = useState(false)
  const [showDefects, setShowDefects] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const [hasUserModifiedMark, setHasUserModifiedMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [scanCounter, setScanCounter] = useState(0)

  const queryClient = useQueryClient()

  const { truckId, ticketId, productEan, data } = location.state

  const {
    data: scanProduct,
    isLoading: isLoadingScanProduct,
    refetch: refetchProduct
  } = useQuery<IProductScanModel>(
    ['product-scan'],
    async () => {
      const resp = await makeCoreGetRequest(
        `/shop-reception-mobile/aleatory/product/scan-by-code/${data.supportId}/${
          productCode ? productCode : productEan
        }`,
        null
      )

      const scanOk = resp.scanSuccess
      const mess = resp.errorMessage
      const scanStatus = resp.scanStatus
      setShowModificaButton(resp.alreadyScanned)

      if (scanOk !== true) {
        formik.setValues({
          productName: '',
          productEans: '',
          productId: null,
          productDlc: null
        })
        showProductScanMessage(mess)
      } else if (ScanStatus.CODE_AND_EAN_NOT_PRESENT === scanStatus) {
        showCodeAndEanNotPresentConfirmation(resp)
      } else if (ScanStatus.EAN_NOT_PRESENT === scanStatus) {
        showEanNotPresentConfirmation(resp)
      } else {
        if (resp.sgr) {
          showSgrMessage()
        }
        populateScanInfo(resp)
        await formik.setFieldValue('bulkProductCode', '')
      }

      setScanCounter(prev => prev + 1)

      return resp || []
    },
    {
      cacheTime: 0,
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      },
      enabled: false
    }
  )

  useEffect(() => {
    return () => {
      queryClient.removeQueries('product-scan')
    }
  }, [queryClient])

  const productScanById = async id => {
    if (!id) {
      return
    }

    try {
      const resp = await makeCoreGetRequest(
        '/shop-reception-mobile/aleatory/product/scan-by-id/' + data.supportId + '/' + id,
        null
      )

      const scanOk = resp.scanSuccess
      const mess = resp.errorMessage
      const scanStatus = resp.scanStatus
      setShowModificaButton(resp.alreadyScanned)

      if (scanOk !== true) {
        formik.setValues({
          productName: '',
          productEans: '',
          productId: null,
          productDlc: null
        })
        showProductScanMessage(mess)
      } else if (ScanStatus.CODE_AND_EAN_NOT_PRESENT === scanStatus) {
        showCodeAndEanNotPresentConfirmation(resp)
      } else if (ScanStatus.EAN_NOT_PRESENT === scanStatus) {
        showEanNotPresentConfirmation(resp)
      } else {
        if (resp.sgr) {
          showSgrMessage()
        }
        populateScanInfo(resp)
      }

      setScanCounter(prev => prev + 1)
    } catch (error) {
      showNotification({
        message: `Error while scanning product. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...scanProduct,
      conform: scanProduct?.validated || 0,
      defect: scanProduct?.broken || 0,
      productDlc: scanProduct?.dlc,
      showDlc: scanProduct?.dlc !== null || scanProduct?.shortDlc === true,
      bulkProductCode: '',
      currentConformValue: 0,
      currentDefectValue: 0
    },
    onSubmit: async (values: any) => {
      const dlcDate = dayjs(values.dlc)
      const formattedDlc = values.dlc && dlcDate.isValid() ? dlcDate.format('YYYY-MM-DD') : null

      let mark = markOptions.find(option => option.value === values.mark)
      if (!mark) {
        mark = markOptions.find(option => option.label === values.mark)
      }

      let defectReason = defectReasonOptions.find(option => option.value === values.defectReason)
      if (!defectReason) {
        defectReason = defectReasonOptions.find(option => option.label === values.defectReason)
      }

      try {
        const product = {
          id: values.productId,
          name: values.productName,
          code: values.productCode,
          ean: values.productEans,
          eanReceived: values.eanReceived,
          unit: null,
          integral: parseFloat(values.currentConformValue) + parseFloat(values.conform) || 0,
          broken: parseFloat(values.currentDefectValue) + parseFloat(values.defect) || 0,
          quantity: values.quantity,
          shortDlc: values.shortDlc,
          dlc: formattedDlc,
          defectReason: defectReason?.value || null,
          mark: mark?.value || null,
          shopReceptionDeviation: values.shopReceptionDeviation,
          supportId: data.supportId
        }
        const resp = await makeCorePostRequest(
          `/shop-reception-mobile/aleatory/product/save`,
          product
        )

        if (resp.shortLifetime) {
          showShortLifeMessage(resp)
        } else {
          clearData()
          document.getElementById('scan-input').focus()

          if (resp.counter == null) {
            showNotification({
              message: 'Cantitaţile pentru produsul necomandat au fost înregistrate cu succes.',
              type: 'success'
            })
          } else {
            setConfirmResponse({ counter: resp.counter, total: resp.total })
            showNotification({
              message: 'Cantitaţile au fost înregistrate cu succes.',
              type: 'success'
            })
          }
        }
      } catch (error) {
        showNotification({
          message: `Error while submitting. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const confirmProduct = async () => {
    const productForm = formik.values
    let wrongDlc = false

    const currentConformValue = parseFloat(productForm.currentConformValue) || 0
    const currentDefectValue = parseFloat(productForm.currentDefectValue) || 0
    const oldConformValue = parseFloat(productForm.conform) || 0
    const oldDefectValue = parseFloat(productForm.defect) || 0
    const currentTotalValue =
      currentConformValue + oldConformValue + currentDefectValue + oldDefectValue

    if (productForm.showDlc && productForm.dlc == null) {
      setIsMandatoryDlcAlertOpen(true)
      return
    }

    const { dlc: entered, productDlc: original } = formik.values

    const formattedEntered = entered ? dayjs(entered).format('YYYY-MM-DD') : null

    if (
      currentConformValue > 0 &&
      productForm.showDlc &&
      productForm.shortDlc === true &&
      productForm.productDlc != null
    ) {
      if (formattedEntered !== original) {
        wrongDlc = true
      }
    }

    const deviation =
      productForm.shopReceptionDeviation === null ? 0 : productForm.shopReceptionDeviation / 1000
    if (
      productForm.quantity != null &&
      !(
        productForm.quantity - deviation <= currentTotalValue &&
        productForm.quantity + deviation >= currentTotalValue
      )
    ) {
      showWrongQuantitiesMessage(wrongDlc)
    } else if (wrongDlc) {
      setIsWrongDlcAlertOpen(true)
    } else {
      const errors = await formik.validateForm();
      if (Object.keys(errors).length) {
        showNotification({ message: Object.values(errors)[0] as string || 'Formular invalid.', type: 'error' });
        return;
      }

      formik.handleSubmit()
    }
  }

  const clearData = () => {
    setLitigation(null)
    setShowMarks(false)
    setShowDefects(false)
    setImageUploaded(false)
    setShowModificaButton(false)
    setProductCode('')
    formik.resetForm()
    formik.setFieldValue('productName', '')
    formik.setFieldValue('productId', '')
    formik.setFieldValue('productEans', '')
    formik.setFieldValue('bulkProductCode', '')
  }

  useEffect(() => {
    if (productEan) {
      setProductCode(productEan)
      refetchProduct()
    }
  }, [productEan])

  useEffect(() => {
    if (data?.sgrProductsList?.length > 0) {
      let sgrProducts: string[] = []

      data?.sgrProductsList?.forEach(product => {
        sgrProducts?.push(product.name)
      })

      showSgrProductsList(sgrProducts)
    }
  }, [data])

  useEffect(() => {
    calculateLitigationType()
  }, [
    formik.values.currentConformValue,
    formik.values.currentDefectValue,
    formik.values.conform,
    formik.values.defect,
    formik.values.quantity,
    formik.values.shopReceptionDeviation,
    scanCounter
  ])

  useEffect(() => {
    if (hasUserModifiedMark) {
      populateDefectReasons(formik.values.mark)
      setHasUserModifiedMark(false)
    }
  }, [formik.values.mark])

  useEffect(() => {
    if (scanCounter > 0) {
      setTimeout(() => {
        document.getElementById("current-conform-input")?.focus()
      }, 0)
    }
  }, [scanCounter])

  const calculateLitigationType = () => {
    const currentConformValue = parseFloat(formik.values.currentConformValue) || 0
    const currentDefectValue = parseFloat(formik.values.currentDefectValue) || 0
    const oldConformValue = parseFloat(formik.values.conform) || 0
    const oldDefectValue = parseFloat(formik.values.defect) || 0
    const totalConform = oldConformValue + currentConformValue
    const totalValue = oldConformValue + oldDefectValue + currentConformValue + currentDefectValue
    const deviation = !formik.values.shopReceptionDeviation
      ? 0
      : formik.values.shopReceptionDeviation / 1000

    const qty = formik.values.quantity;

    if (qty == null) {
      setLitigation(null);
      setShowMarks(false);
      setShowDefects(false);
      formik.setFieldValue('mark', '');
      formik.setFieldValue('defectReason', '');
      return;
    }

    if (
      formik.values.quantity > totalConform + deviation &&
      formik.values.quantity === totalValue
    ) {
      setMarks([
        Mark.VEGETABLE_FRUIT_QUALITY,
        Mark.EXPIRED,
        Mark.SHORT_TERM,
        Mark.DAMAGED,
        Mark.VEGETABLES_FRUITS,
        Mark.DETERIORATION_A
      ])

      setLitigation(2)
      setShowMarks(true)
    } else if (
      formik.values.quantity > totalConform + deviation &&
      formik.values.quantity !== totalValue
    ) {
      setMarks([
        Mark.PALLETS_MERCHANDISE_MISSING,
        Mark.MISSING_CARRIER,
        Mark.QUANTITATIVE_DIFFERENCE
      ])

      setLitigation(1)
      setShowMarks(true)
    } else if (formik.values.quantity < totalConform - deviation) {
      setMarks([Mark.PALLETS_MERCHANDISE_MISSING, Mark.QUANTITATIVE_DIFFERENCE])

      setLitigation(1)
      setShowMarks(true)
    } else if (
      formik.values.quantity - deviation <= totalConform &&
      totalConform <= formik.values.quantity + deviation
    ) {
      setLitigation(null)
      setShowMarks(false)
      setShowDefects(false)
      setMarks([])
    }

    formik.setFieldValue('mark', '')
    formik.setFieldValue('defectReason', '')
  }

  const populateDefectReasons = selectedMark => {
    if (formik.values.mark) {
      formik.setFieldValue('defectReason', '')

      switch (litigation) {
        case 1:
          setDefectReasons([DefectReason.PROVIDER])
          formik.setFieldValue('defectReason', DefectReason.PROVIDER)
          break
        case 2:
          if (selectedMark === Mark.EXPIRED || selectedMark === Mark.SHORT_TERM) {
            setDefectReasons([DefectReason.PROVIDER])
            formik.setFieldValue('defectReason', DefectReason.PROVIDER)
          } else if (selectedMark === Mark.VEGETABLE_FRUIT_QUALITY) {
            setDefectReasons([DefectReason.PROVIDER, DefectReason.SHOP])
          } else if (selectedMark === Mark.DAMAGED) {
            setDefectReasons([DefectReason.PROVIDER, DefectReason.TRANSPORTER, DefectReason.SHOP])
          } else if (
            selectedMark === Mark.VEGETABLES_FRUITS ||
            selectedMark === Mark.DETERIORATION_A
          ) {
            setDefectReasons([DefectReason.PROVIDER, DefectReason.TRANSPORTER])
          }
          break
        default:
          setDefectReasons([])
      }
    }
  }

  const populateScanInfo = resp => {
    const conform = resp.validated === null ? 0 : resp.validated
    const defect = resp.broken === null ? 0 : resp.broken
    const deviation = resp.shopReceptionDeviation ? resp.shopReceptionDeviation : 0

    setShowMarks(false)
    setShowDefects(false)

    formik.setValues(prev => ({
      ...prev,
      productCode: resp.productCode,
      productName: resp.productName,
      productEans: resp.productEans,
      productDlc: resp.dlc,
      productId: resp.productId,
      quantity: resp.quantity ?? null,
      eanReceived: resp.eanReceived,
      shopReceptionDeviation: deviation,
      alreadyScanned: !!resp.alreadyScanned,
      conform: conform,
      defect: defect,
      dlc: resp.dlc,
      shortDlc: resp.shortDlc,
      defectReason: resp.defectReason,
      mark: resp.mark,
      showDlc: resp.dlc !== null || resp.shortDlc === true,
      bulkProductCode: ''
    }))
  }

  const onFinalize = () => {
    if (confirmResponse.counter !== confirmResponse.total) {
      makeCoreGetRequest('/shop-reception-mobile/aleatory/missing/' + data.supportId, null).then(
        resp => {
          const products = resp
          if (products.length > 0) {
            showMissingProductsConfirm(products.map(prod => prod))
          } else {
            doFinalise()
          }
        }
      )
    } else {
      doFinalise()
    }
  }

  const doFinalise = async () => {
    if (isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)

    try {
      await makeCorePostRequest(
        `/shop-reception-mobile/aleatory/support/close/${truckId}`,
        { supportId: data.supportId },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      ).then(() => {
        clearData()
        navigate(URLS.AUTH_DELIVERY_COMPLETE_SCAN_STEP_1, { state: { truckId, ticketId } })
      })
    } catch (error) {
      showNotification({
        message: `Error while submitting. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }

  const modifyQuantities = () => {
    navigate(URLS.AUTH_DELIVERY_RANDOM_PRODUCT_CONFIRM, {
      state: {
        productId: formik.values.productId,
        supportId: data.supportId,
        truckId: truckId,
        ticketId: ticketId,
        flow: 'integral',
        quantity: formik.values.quantity
      }
    })
  }

  const onScan = useCallback((code: string) => {
    clearData()
    setProductCode(code)
    if (validateScanCode(showNotification, code, 'produs')) {
      setTimeout(refetchProduct, 10)
    }
    setTimeout(() => document.getElementById('scan-input')?.focus(), 50)
  }, [clearData, showNotification, refetchProduct])

  const scanDecimalChange = useMemo(
    () => makeScanAwareDecimalChange(formik.setFieldValue, { threshold: 5, onScan, getValue: (f) => (formik.values as any)[f] }),
    [formik.setFieldValue, onScan, formik.values]
  )

  const handleConformChange = scanDecimalChange('currentConformValue')
  const handleDefectChange  = scanDecimalChange('currentDefectValue')

  const productOptions = useMemo(
    () => toProductOptions(data?.productList),
    [data?.productList]
  )

  const selectedOption = useMemo(
    () => findSelectedOption(productOptions, formik.values.bulkProductCode),
    [productOptions, formik.values.bulkProductCode]
  )

  const showWrongQuantitiesMessage = wrongDlc => {
    setWrongQuantitiesModalContent({
      title: 'Atenție!',
      description: 'Există o diferenţă între cantitatea asteptată şi cea introdusă.',
      buttons: [
        {
          label: 'Mai verific',
          onClick: () => {
            setIsWrongQuantitiesModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input').focus()
            }, 0)
          },
          color: 'primary'
        },
        {
          label: 'Am terminat',
          onClick: () => {
            setIsWrongQuantitiesModalOpen(false)

            if (wrongDlc) {
              setIsWrongDlcAlertOpen(true)
            } else {
              formik.handleSubmit()
            }
          },
          color: 'primary'
        }
      ]
    })
    setIsWrongQuantitiesModalOpen(true)
  }

  const closeWrongQuantitiesMessage = () => {
    setIsWrongQuantitiesModalOpen(false)
  }

  const showProductScanMessage = msg => {
    setProductScanModalContent({
      title: 'Atenție!',
      description: msg,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsProductScanModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input').focus()
            }, 0)
          },
          color: 'primary'
        }
      ]
    })
    setIsProductScanModalOpen(true)
  }

  const closeProductScanMessage = () => {
    setIsProductScanModalOpen(false)
  }

  const showShortLifeMessage = resp => {
    setShortLifeModalContent({
      title: 'Atenție!',
      description: "Produsul are durată de valabilitate scurtă!",
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsShortLifeModalOpen(false)
            clearData()
            setConfirmResponse({ counter: resp.counter, total: resp.total })
            showNotification({
              message: 'Cantitaţile au fost înregistrate cu succes.',
              type: 'success'
            })
            setTimeout(() => {
              document.getElementById('scan-input').focus()
            }, 0)
          },
          color: 'primary'
        }
      ]
    })
    setIsShortLifeModalOpen(true)
  }

  const closeShortLifeMessage = () => {
    setIsShortLifeModalOpen(false)
  }

  const showSgrProductsList = sgrProducts => {
    setSgrProductsModalContent({
      title: 'Pe acest suport există produse SGR!',
      listItems: sgrProducts,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsSgrProductsModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsSgrProductsModalOpen(true)
  }

  const closeSgrProductsModal = () => {
    setIsSgrProductsModalOpen(false)
  }

  const showSgrMessage = () => {
    setSgrMessageModalContent({
      title:
        'Acesta este un produs cu ambalaj returnabil - SGR! Va rugam sa gestionati aceste produs separat in vederea expunerii la raft.',
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsSgrMessageModalOpen(false)
            setTimeout(() => document.getElementById("current-conform-input")?.focus(), 0)
          },
          color: 'primary'
        }
      ]
    })
    setIsSgrMessageModalOpen(true)
  }

  const closeSgrMessage = () => {
    setIsSgrMessageModalOpen(false)
  }

  const showCodeAndEanNotPresentConfirmation = (resp) => {
    setCodeAndEanNotPresentModalContent({
      title: 'Produsul nu a fost comandat. Continuaţi recepţia acestuia?',
      buttons: [
        {
          label: 'Da',
          onClick: () => {
            populateScanInfo(resp)
            setIsCodeAndEanNotPresentModalOpen(false)
            setTimeout(() => document.getElementById("current-conform-input")?.focus(), 0)
          },
          color: 'primary'
        },
        {
          label: 'Nu',
          onClick: () => {
            clearData()
            setIsCodeAndEanNotPresentModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input').focus()
            }, 0)
          },
          color: 'secondary'
        }
      ]
    })
    setIsCodeAndEanNotPresentModalOpen(true)
  }

  const closeCodeAndEanNotPresentConfirmation = () => {
    setIsCodeAndEanNotPresentModalOpen(false)
  }

  const showEanNotPresentConfirmation = resp => {
    setEanNotPresentModalContent({
      title: 'EAN-ul nu există în fişierul de la depozit. Continuaţi recepţia acestuia?',
      buttons: [
        {
          label: 'Da',
          onClick: () => {
            populateScanInfo(resp)
            setIsEanNotPresentModalOpen(false)
            setTimeout(() => document.getElementById("current-conform-input")?.focus(), 0)
          },
          color: 'primary'
        },
        {
          label: 'Nu',
          onClick: () => {
            clearData()
            setIsEanNotPresentModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input').focus()
            }, 0)
          },
          color: 'secondary'
        }
      ]
    })
    setIsEanNotPresentModalOpen(true)
  }

  const closeEanNotPresentConfirmation = () => {
    setIsEanNotPresentModalOpen(false)
  }

  const showMissingProductsConfirm = listItems => {
    setMissingProductsConfirmModalContent({
      title: 'Următoarele produse nu au fost validate:',
      listItems: listItems,
      buttons: [
        {
          label: 'Mai verific',
          onClick: () => {
            setIsMissingProductsConfirmModalOpen(false)
            setTimeout(() => {
              document.getElementById('scan-input').focus()
            }, 0)
          },
          color: 'primary',
          disabled: isSubmitting
        },
        {
          label: 'Am terminat',
          onClick: () => {
            doFinalise()
            setIsMissingProductsConfirmModalOpen(false)
          },
          color: 'secondary',
          disabled: isSubmitting
        }
      ]
    })
    setIsMissingProductsConfirmModalOpen(true)
  }

  const closeMissingProductsConfirm = () => {
    setIsMissingProductsConfirmModalOpen(false)
  }

  if (isLoadingScanProduct) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  return (
    <Grid container justifyContent={'center'} rowSpacing={2}>
      <Grid item xs={10}>
        <TitleHeader title="VALIDARE LIVRARE SCANARE INTEGRALǍ" />
      </Grid>

      <Grid item xs={10} mb={2}>
        <Paper elevation={3} style={{ padding: '20px' }} onClick={() => {}}>
          <Typography variant="medium" component="h5">
            SCANARE INTEGRALA
          </Typography>
          <Typography variant="medium" component="p">
            Numar inmatriculare: {data?.truckNumber}
          </Typography>
          <Typography variant="medium" component="p">
            BL: {data?.ticketNumber}
          </Typography>
          <Typography variant="medium" component="p">
            Suporti verificati: {data?.supportCounter}/{data?.totalSupportNumber}
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={10} mb={2}>
        <Paper elevation={3} style={{ padding: '20px' }} onClick={() => {}}>
          <Typography variant="medium" component="h5">
            Eticheta suport:
          </Typography>
          <Typography variant="medium" component="p">
            {data?.supportCode}
          </Typography>
          <Typography variant="medium" component="p">
            Numar suport: {data?.supportNumber}
          </Typography>
          <Typography variant="medium" component="p">
            Produse verificate:{' '}
            {data?.productCounter > confirmResponse?.counter
              ? data?.productCounter
              : confirmResponse?.counter}
          </Typography>
        </Paper>
      </Grid>

      {data?.productList && data?.productList?.length > 0 && (
        <Grid item xs={10} mb={2}>
          <Autocomplete
            options={productOptions}
            value={selectedOption}
            inputValue={inputValue}
            onInputChange={(_e, newVal) => {
              setInputValue(newVal)
            }}
            onChange={(_e, newOption) => {
              clearData()
              const id = newOption?.value ?? ''
              formik.setFieldValue('bulkProductCode', id)
              productScanById(id)
            }}
            filterOptions={(opts, { inputValue }) =>
              opts.filter(o => o.label.toLowerCase().includes(inputValue.toLowerCase()))
            }
            getOptionLabel={o => o.label}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={params => (
              <TextField
                {...params}
                label="Selectează produs vrac"
                variant="outlined"
                error={!!formik.errors.bulkProductCode}
                helperText={formik.errors.bulkProductCode as string}
                fullWidth
              />
            )}
            disableClearable
            autoHighlight
            fullWidth
          />
        </Grid>
      )}

      <Grid item xs={10}>
        <FlexCenter>
          <Input
            type="text"
            variant="standard"
            label="Cod produs"
            autoFocus
            width="100%"
            id="scan-input"
            value={productCode}
            onChange={e => {
              const target = e.target as HTMLInputElement
              setProductCode(target.value)
            }}
            onKeyUp={event => {
              if (event.which == 0) {
                setProductCode('')
                if (productCode && validateScanCode(showNotification, productCode, 'produs'))
                  refetchProduct()
              }
            }}
          />
          <IconButton
            onClick={() => {
              if (validateScanCode(showNotification, productCode, 'produs')) {
                refetchProduct()
              }
            }}
          >
            <ManageSearchIcon />
          </IconButton>
        </FlexCenter>
      </Grid>

      <Grid item xs={10} my={2}>
        {formik.values.productEans && (
          <>
            <TableContainer>
              <Table>
                <TableHead style={{ backgroundColor: '#0970E6' }}>
                  <TableRow>
                    <TableCell style={{ color: 'white' }}>Produs</TableCell>
                    <TableCell style={{ color: 'white' }}>Calitativ</TableCell>
                    <TableCell style={{ color: 'white' }}>Defect</TableCell>
                    <TableCell style={{ color: 'white' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {(() => {
                        const fullText = `${formik.values?.productName || ''} ${
                          formik.values?.productEans || ''
                        }`
                        return fullText.length > 50 ? `${fullText.slice(0, 50)}...` : fullText
                      })()}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        variant="outlined"
                        value={formik.values.currentConformValue || 0}
                        onChange={handleConformChange}
                        id="current-conform-input"
                        width="100%"
                        style={{ minWidth: '55px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        variant="outlined"
                        value={formik.values.currentDefectValue || 0}
                        onChange={handleDefectChange}
                        width="100%"
                        style={{ minWidth: '55px' }}
                      />
                    </TableCell>
                  </TableRow>

                  {litigation === 2 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0, textAlign: 'center' }}>
                        <Box>
                          <ImageUpload
                            uploadUrl={`/shop-reception-mobile/integral/upload/${data.supportId}/${formik.values.productId}`}
                            onSuccess={() => {
                              showNotification({
                                message: `Imaginea a fost incarcata cu succes.`,
                                type: 'success'
                              })
                              setImageUploaded(true)
                            }}
                            onError={() =>
                              showNotification({
                                message: `A aparut o eroare la incarcarea imaginii.`,
                                type: 'error'
                              })
                            }
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid item xs={12}>
              <DatePicker
                style={{ width: '100%' }}
                value={dayjs(formik.values.dlc)}
                onChange={value => formik.setFieldValue('dlc', value as Date | string)}
                hidden={!formik.values.showDlc}
                adapterLocale="ro"
                modalLabel="Selecteaza data"
                closeButtonLabel="Anuleaza"
                confirmButtonLabel="Confirma"
              />
            </Grid>
            <Grid container>
              {showMarks && (
                <Grid item xs={12}>
                  <Input
                    type="select"
                    label="Marca"
                    variant="outlined"
                    width="100%"
                    value={formik.values.mark}
                    values={marks?.map(mark => ({ value: mark, label: mark }))}
                    onChange={event => {
                      formik.setFieldValue('mark', (event.target as HTMLInputElement).value)
                      setShowDefects(true)
                      setHasUserModifiedMark(true)
                    }}
                    error={!!formik.errors.mark}
                    helperText={formik.errors.mark as string}
                  />
                </Grid>
              )}
              {showDefects && (
                <Grid item xs={12}>
                  <Input
                    type="select"
                    label="Motiv"
                    variant="outlined"
                    width="100%"
                    value={formik.values.defectReason}
                    values={defectReasons?.map(defect => ({ value: defect, label: defect }))}
                    onChange={event => {
                      formik.setFieldValue('defectReason', (event.target as HTMLInputElement).value)
                    }}
                    disabled={formik.values.mark === ''}
                    error={!!formik.errors.defectReason}
                    helperText={formik.errors.defectReason as string}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Grid>

      <Grid xs={10} mt={2} item>
        {formik.values.productEans && (
          <Button
            type="button"
            variant="contained"
            label="CONFIRMĂ CANTITĂȚI"
            onClick={() => confirmProduct()}
            style={{ width: '100%' }}
            disabled={
              (litigation === 2 &&
                (!imageUploaded ||
                  formik.values.mark === '' ||
                  formik.values.defectReason === '')) ||
              (litigation === 1 &&
                (formik.values.mark === '' || formik.values.defectReason === '')) ||
              formik.isSubmitting
            }
          />
        )}
        {showModificaButton && (
          <Button
            type="button"
            variant="contained"
            label="Modifică cantităţi"
            onClick={modifyQuantities}
            style={{ width: '100%', marginTop: '15px' }}
          />
        )}
        {!formik.values.productEans && (
          <Button
            type="button"
            variant="contained"
            label="FINALIZEAZĂ SUPORT"
            onClick={() => onFinalize()}
            style={{ width: '100%', marginTop: '15px' }}
            disabled={isSubmitting}
          />
        )}
      </Grid>

      <>
        <AlertModal
          open={isWrongQuantitiesModalOpen}
          handleClose={closeWrongQuantitiesMessage}
          title={wrongQuantitiesModalContent.title}
          description={wrongQuantitiesModalContent.description}
          buttons={wrongQuantitiesModalContent.buttons}
        />
        <AlertModal
          open={isMandatoryDlcAlertOpen}
          handleClose={() => setIsMandatoryDlcAlertOpen(false)}
          title={'Atentie'}
          description={'Data limită de consum este obligatorie!'}
          buttons={[
            { label: 'Ok', onClick: () => setIsMandatoryDlcAlertOpen(false), color: 'primary' }
          ]}
        />
        <AlertModal
          open={isWrongDlcAlertOpen}
          handleClose={() => setIsWrongDlcAlertOpen(false)}
          title={'Atentie'}
          description={'Există diferenţe între DLC asteptat şi cel introdus.'}
          buttons={[
            {
              label: 'Mai verific',
              onClick: () => {
                setIsWrongDlcAlertOpen(false)
                setTimeout(() => {
                  document.getElementById('scan-input').focus()
                }, 0)
              },
              color: 'primary'
            },
            {
              label: 'Am terminat',
              onClick: () => {
                formik.handleSubmit()
                setIsWrongDlcAlertOpen(false)
              },
              color: 'primary'
            }
          ]}
        />
        <AlertModal
          open={isSgrProductsModalOpen}
          handleClose={closeSgrProductsModal}
          title={sgrProductsModalContent.title}
          listItems={sgrProductsModalContent.listItems}
          buttons={sgrProductsModalContent.buttons}
        />
        <AlertModal
          open={isProductScanModalOpen}
          handleClose={closeProductScanMessage}
          title={productScanModalContent.title}
          description={productScanModalContent.description}
          buttons={productScanModalContent.buttons}
        />
        <AlertModal
          open={isCodeAndEanNotPresentModalOpen}
          handleClose={closeCodeAndEanNotPresentConfirmation}
          title={codeAndEanNotPresentModalContent.title}
          buttons={codeAndEanNotPresentModalContent.buttons}
        />
        <AlertModal
          open={isEanNotPresentModalOpen}
          handleClose={closeEanNotPresentConfirmation}
          title={eanNotPresentModalContent.title}
          buttons={eanNotPresentModalContent.buttons}
        />
        <AlertModal
          open={isSgrMessageModalOpen}
          handleClose={closeSgrMessage}
          title={sgrMessageModalContent.title}
          buttons={sgrMessageModalContent.buttons}
        />
        <AlertModal
          open={isMissingProductsConfirmModalOpen}
          handleClose={closeMissingProductsConfirm}
          title={MissingProductsConfirmModalContent.title}
          listItems={MissingProductsConfirmModalContent.listItems}
          buttons={MissingProductsConfirmModalContent.buttons}
        />
        <AlertModal
          open={isShortLifeModalOpen}
          handleClose={closeShortLifeMessage}
          title={shortLifeModalContent.title}
          description={shortLifeModalContent.description}
          buttons={shortLifeModalContent.buttons}
        />
      </>
    </Grid>
  )
}
export default DeliveryCompleteScanStep2
